import { useState } from 'react'

import { ChevronDown } from 'lucide-react'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useProject } from '@/contexts/ProjectContext'

import './ProjectSwitcher.css'

/**
 * Top-bar combobox for the project in view — searchable by number or name,
 * which matters once the warehouse feed delivers the real project list.
 * Popover + Command per the shadcn combobox pattern. The list is
 * fetched from the API by ProjectProvider, so loading/unavailable states
 * render as a static chip instead of an empty combobox.
 */
function ProjectSwitcher() {
  const { projects, currentProject, status, setProject } = useProject()
  const [open, setOpen] = useState(false)

  if (status === 'loading') {
    return (
      <span className="proj__chip proj__chip--static" role="status">
        <span className="proj__name">Loading projects…</span>
      </span>
    )
  }

  if (status === 'error' || !currentProject) {
    return (
      <span className="proj__chip proj__chip--static" role="status">
        <span className="proj__name">
          {status === 'error' ? 'Projects unavailable' : 'No projects'}
        </span>
      </span>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className="proj__chip"
        role="combobox"
        aria-expanded={open}
        aria-label="Switch project"
      >
        <span className="proj__num mono">{currentProject.id}</span>
        <span className="proj__name">{currentProject.name}</span>
        <ChevronDown size={15} aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search projects…" />
          <CommandList>
            <CommandEmpty>No project found.</CommandEmpty>
            <CommandGroup>
              {projects.map((project) => (
                <CommandItem
                  key={project.id}
                  value={`${project.id} ${project.name}`}
                  // Highlight in the same selected blue the sidebar uses
                  // (preset default is gray bg-muted). data-checked drives
                  // the item's own far-right CheckIcon. min-h-11 = the 44px
                  // touch-target minimum (React_Web_Standards §11.1).
                  className="min-h-11 data-selected:bg-(--surface-selected) data-selected:text-(--blattner-dark-blue)"
                  data-checked={project.id === currentProject.id}
                  onSelect={() => {
                    setProject(project.id)
                    setOpen(false)
                  }}
                >
                  <span className="mono">{project.id}</span>
                  {project.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default ProjectSwitcher
